import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Pagination } from '@/components/Pagination';

import { useAdminUsers } from '@/hooks/useAdminUsers';

import type { IAdminUser } from '@/types/user';

import { UserManagementTable } from './UserManagementTable';

function UsersTab() {
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [cursorHistory, setCursorHistory] = useState<(string | null)[]>([null]);
  const [sortBy, setSortBy] = useState<'name'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const exclusiveStartKey = cursorHistory[currentPage - 1] || null;
  const encodedCursor = exclusiveStartKey ? encodeURIComponent(exclusiveStartKey) : undefined;

  const { data, loading, error } = useAdminUsers({
    search: '',
    status: 'active',
    employmentType: 'all',
    limit: 25,
    sortOrder,
    exclusiveStartKey: encodedCursor,
  });

  useEffect(() => {
    if (data?.nextCursor && currentPage === cursorHistory.length) {
      // oxlint-disable-next-line react/set-state-in-effect
      setCursorHistory((prev) => [...prev, data.nextCursor]);
    }
  }, [data?.nextCursor, currentPage, cursorHistory.length]);

  const resetPagination = useCallback(() => {
    setCurrentPage(1);
    setCursorHistory([null]);
  }, []);

  const handleSort = useCallback(
    (column: string, newSortOrder: 'asc' | 'desc') => {
      if (column === 'name') {
        setSortBy('name');
        setSortOrder(newSortOrder);
        resetPagination();
      }
    },
    [resetPagination],
  );

  const handleUserClick = useCallback(
    (selectedUser: IAdminUser) => {
      void navigate(`/admin/users/${selectedUser.id}`, { state: { user: selectedUser } });
    },
    [navigate],
  );

  const handlePageChange = useCallback((newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  return (
    <div className="rounded-lg bg-white shadow-md">
      {error ? (
        <div className="p-8 text-center">
          <div className="mb-2 text-lg font-medium text-red-500">Error loading users</div>
          <p className="text-gray-600">{error}</p>
        </div>
      ) : (
        <>
          <UserManagementTable
            users={data?.items || []}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            onUserClick={handleUserClick}
            loading={loading}
            emptyMessage={
              !loading && (!data || data.items.length === 0) ? (
                <>
                  <div className="mb-4 text-6xl">👥</div>
                  <h3 className="mb-2 text-lg font-medium text-gray-900">No users found</h3>
                  <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
                </>
              ) : undefined
            }
          />
          <Pagination
            currentPage={currentPage}
            hasMore={data?.nextCursor != null}
            hasPrevious={currentPage > 1}
            pageSize={25}
            currentItemsCount={data?.items.length || 0}
            onPageChange={handlePageChange}
          />
        </>
      )}
    </div>
  );
}

export { UsersTab };
